import { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Chip } from "@mui/material";
import { Pencil } from "lucide-react";
import { Document, DocumentType } from "protogen/documents_pb";
import {
  UpdateDocumentRequest,
  ListDocumentTopicsRequest,
} from "protogen/documents_service_pb";
import { useUpdateDocument, useListDocumentTopics } from "services/documents";
import useIsMobile from "components/hooks/useIsMobile";
import { CurrentUserContext } from "components/context/RequireAuth";
import TextField from "components/common/TextField";
import { isUserAdmin } from "common/userUtils";

export default ({
  initialDocument,
  editable,
  clickable = true,
}: {
  initialDocument: Document;
  editable: boolean;
  clickable?: boolean;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { request: updateDocumentRequest } = useUpdateDocument();
  const { request: listDocumentTopicsRequest } = useListDocumentTopics();
  const [document, setDocument] = useState(initialDocument);
  const [isEdit, setIsEdit] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [dynamicTopics, setDynamicTopics] = useState<string[]>([]);
  const [topicsLoaded, setTopicsLoaded] = useState(false);
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const autocompleteRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDocument(initialDocument);
  }, [initialDocument]);

  useEffect(() => {
    if (isEdit) {
      setAutocompleteOpen(true);
      // Lazily load topics when user starts editing
      if (!topicsLoaded) {
        loadTopics();
      }
      // Small delay to ensure the Autocomplete is fully rendered
      setTimeout(() => {
        if (autocompleteRef.current) {
          autocompleteRef.current.focus();
          // If there is a current label, select all text
          if (document?.topics?.[0]) {
            autocompleteRef.current.setSelectionRange(
              0,
              autocompleteRef.current.value.length,
            );
          }
        }
      }, 100);
    }
  }, [isEdit]);

  const loadTopics = async () => {
    const response = await listDocumentTopicsRequest(
      new ListDocumentTopicsRequest(),
    );
    if (!response) {
      console.error("Failed to load document topics");
      setTopicsLoaded(true); // Still mark as loaded to avoid retrying
      return;
    }
    // Combine community and user topics, removing duplicates
    const allTopics = Array.from(
      new Set([...response?.communityTopics, ...response?.userTopics]),
    );
    setDynamicTopics(allTopics);
    setTopicsLoaded(true);
  };

  const sx = {
    ...(!isMobile && {
      justifySelf: "end",
    }),
    ...(isMobile && {
      alignSelf: "end",
    }),
    flexShrink: 0,
  };
  const onChange = async (topic: string | null) => {
    let finalTopic = topic;
    if (typeof topic === "string" && topic.startsWith('Add "')) {
      // Extract the user-typed value from 'Add "..."'
      finalTopic = topic.replace(/^Add \"/, "").replace(/\"$/, "");
    }
    if (finalTopic === null) {
      // When X is clicked, clear the topic and keep the autocomplete open
      const response = await updateDocumentRequest(
        new UpdateDocumentRequest({
          documentRef: document?.ref || "",
          shouldUpdateTopics: true,
          updatedTopics: [],
        }),
      );
      if (response?.document) {
        setDocument(response.document);
        setAutocompleteOpen(true);
        setTimeout(() => {
          autocompleteRef.current?.focus();
        }, 100);
      }
      return;
    }

    const response = await updateDocumentRequest(
      new UpdateDocumentRequest({
        documentRef: document?.ref || "",
        shouldUpdateTopics: true,
        updatedTopics: finalTopic ? [finalTopic] : [],
      }),
    );
    if (response?.document) {
      setDocument(response.document);
      setIsEdit(false);
      setAutocompleteOpen(false);
    }
  };

  const handleBlur = () => {
    // Close the autocomplete when user clicks away
    setAutocompleteOpen(false);
    setIsEdit(false);
  };

  if (document.documentType === DocumentType.TASK_BLOCK) {
    return null;
  } else if (!editable && document?.topics?.length === 0) {
    return null;
  } else if (!isEdit && document?.topics?.length > 0) {
    const topic = document?.topics?.[0] || "";
    return (
      <Chip
        size="medium"
        variant="outlined"
        label={topic}
        onClick={
          clickable
            ? () =>
                navigate(
                  `/documents/community/${encodeURIComponent(
                    topic.trim().toLowerCase(),
                  )}`,
                )
            : undefined
        }
        {...(editable && {
          onDelete: () => setIsEdit(true),
          deleteIcon: <Pencil />,
        })}
        sx={{
          cursor: editable ? "pointer" : "default",
          "& .MuiChip-deleteIcon": { height: "16px", width: "16px" },
          ...sx,
        }}
      />
    );
  } else if (isEdit) {
    return (
      <Autocomplete
        freeSolo
        selectOnFocus
        size="small"
        options={[
          // Default topics
          "Gifting guide",
          "Home services",
          "How-tos",
          "Meal planning",
          "Party planning",
          "Research",
          "Template",
          "Travel",
          ...(isAdmin ? ["Faye HQ"] : []),
          // Dynamic topics from API (community and user topics)
          ...dynamicTopics,
        ]
          // Remove duplicates and sort
          .filter(
            (topic, index, arr) =>
              arr.map((t) => t.toLowerCase()).indexOf(topic.toLowerCase()) ===
              index,
          )
          .sort((a, b) => a.localeCompare(b))}
        defaultValue={document?.topics?.[0] || undefined}
        onChange={(_, value) => onChange(value)}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(params.inputValue.toLowerCase()),
          );

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option);
          if (inputValue !== "" && !isExisting) {
            filtered.push(`Add "${inputValue}"`);
          }

          return filtered;
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string" && option.startsWith("Add ")) {
            return option.replace("Add ", "").replace(/"/g, "");
          }
          return option;
        }}
        renderOption={(props, option) => {
          const isAddOption =
            typeof option === "string" && option.startsWith("Add ");
          return (
            <>
              {isAddOption && (
                <hr
                  style={{
                    margin: "8px 0",
                    border: "none",
                    borderTop: "1px solid #e0e0e0",
                  }}
                />
              )}
              <li {...props}>{option}</li>
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder="Label"
            size="small"
            inputRef={autocompleteRef}
          />
        )}
        sx={{
          ...sx,
          width: "160px",
          "& .MuiOutlinedInput-root": {
            minHeight: "fit-content",
          },
          "& input.MuiInputBase-input": {
            marginTop: "0",
          },
          "& .MuiInputBase-root": {
            paddingTop: "4.5px !important",
            paddingBottom: "4.5px !important",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            left: "15px",
            top: "4px",
          },
          "label.MuiFormLabel-root": {
            top: "16px",
          },
        }}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={handleBlur}
        onBlur={handleBlur}
        autoFocus={isEdit}
      />
    );
  } else {
    return (
      <Chip
        onClick={() => setIsEdit(true)}
        size="medium"
        variant="outlined"
        label={"Add label"}
        sx={{
          cursor: "pointer",
          ...sx,
        }}
      />
    );
  }
};
